import { suitOf, type CardId } from './cards';
import type { SeatView } from './projection';
import type { Action, RejectCode } from './types';

export type PlayableAction = Exclude<Action, { type: 'NEXT_HAND' }>;

/**
 * The only legality function (PRD FR-05). Takes a SeatView so the server, practice bots and the
 * client's card highlighting all share it without ever needing hidden information.
 */
export function legalActions(view: SeatView): PlayableAction[] {
  if (view.phase !== 'IN_HAND') return [];
  const hand = view.hand;
  const { trump } = hand;

  if (hand.phase === 'RESERVE') {
    if (view.seat !== trump.reserverSeat || view.myReserved !== null) return [];
    return view.myHand.map((cardId) => ({ type: 'RESERVE_TRUMP', cardId }));
  }

  if (hand.phase !== 'PLAY' || hand.active !== view.seat) return [];

  const cards = legalCards(view);
  const actions: PlayableAction[] = cards.map((cardId) => ({ type: 'PLAY_CARD', cardId }));
  if (canRequestReveal(view)) actions.push({ type: 'REQUEST_REVEAL' });
  return actions;
}

function canRequestReveal(view: SeatView): boolean {
  const hand = view.hand;
  if (hand.trump.mode !== 'BAND_B_REQUEST' || hand.trump.status !== 'HIDDEN') return false;
  if (hand.trick.length === 0) return false;
  const led = suitOf(hand.trick[0]!.cardId);
  return !view.myHand.some((c) => suitOf(c) === led);
}

/** Cards the seat may play right now (empty when it is not their turn). */
export function legalCards(view: SeatView): CardId[] {
  const hand = view.hand;
  if (view.phase !== 'IN_HAND' || hand.phase !== 'PLAY' || hand.active !== view.seat) return [];
  const playable = view.myHand;
  if (hand.trick.length === 0) return [...playable];

  const led = suitOf(hand.trick[0]!.cardId);
  const follow = playable.filter((c) => suitOf(c) === led);
  if (follow.length > 0) return follow;

  if (
    hand.trump.mode === 'BAND_B_REQUEST' &&
    hand.trump.requestedBy === view.seat &&
    hand.trump.suit
  ) {
    const trumps = playable.filter((c) => suitOf(c) === hand.trump.suit);
    if (trumps.length > 0) return trumps;
  }
  return [...playable];
}

/** Why a card cannot be played — drives the disabled-card tooltip (PRD user story). */
export function explainIllegal(view: SeatView, cardId: CardId): RejectCode | null {
  if (view.phase === 'MATCH_OVER') return 'MATCH_OVER';
  if (view.phase !== 'IN_HAND') return 'WRONG_PHASE';
  const hand = view.hand;
  if (cardId === view.myReserved) return 'CARD_RESERVED';
  if (!view.myHand.includes(cardId)) return 'NOT_IN_HAND';
  if (hand.phase === 'RESERVE') {
    return view.seat === hand.trump.reserverSeat ? null : 'NOT_RESERVER';
  }
  if (hand.phase !== 'PLAY') return 'WRONG_PHASE';
  if (hand.active !== view.seat) return 'NOT_YOUR_TURN';
  if (legalCards(view).includes(cardId)) return null;
  const led = hand.trick.length > 0 ? suitOf(hand.trick[0]!.cardId) : null;
  if (led !== null && view.myHand.some((c) => suitOf(c) === led)) return 'MUST_FOLLOW_SUIT';
  if (hand.trump.requestedBy === view.seat) return 'MUST_PLAY_TRUMP';
  return 'ILLEGAL_ACTION';
}
